import { Link, useNavigate } from '@tanstack/react-router';
import { REVIEW_REASONS } from '@pf/contracts';
import { AlarmClock, ClipboardCheck, Hand, Search, ShieldAlert, X } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReviewQueueRow } from '@pf/contracts';
import { useCaseClaim, useReviewQueue } from '@/lib/api/ops-queries';
import { nowMs, toEpochMs } from '@/lib/clock';
import { formatAge } from '@/lib/datetime';
import { readParam, useListState } from '@/lib/nav/list-state';
import { REVIEW_LIST, REVIEW_SORTS } from '@/lib/nav/ops-lists';
import { ALL_WEBS, useWebScope } from '@/lib/scope/web-scope';
import { REVIEW_REASON_META } from '@/lib/states';
import { cn, shortId } from '@/lib/utils';
import { SlaCountdown } from '@/components/domain/countdown-timer';
import { ParticipantPair } from '@/components/domain/participant-chip';
import { WebScopeScopedNotice } from '@/components/domain/web-scope-select';
import { MoneyDisplay } from '@/components/domain/money-display';
import { StateBadge } from '@/components/domain/state-badge';
import { DataTable, type Column } from '@/components/layout/data-table';
import { PageHeader } from '@/components/layout/page';
import { SortControl } from '@/components/layout/sort-control';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/ui/feedback';
import {
  Input,
  Label,
  Pill,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from '@/components/ui/primitives';

/** A sort key from the table header or the mobile control, ignored unless the queue offers it. */
const readSort = readParam.oneOf(REVIEW_SORTS);

/**
 * Review queue.
 *
 * The queue is sorted by SLA deadline, not by arrival, because that is the order in which the
 * work actually needs doing — a two-hour-old case with a breached SLA outranks a ten-minute-old
 * one. Breaches count up rather than showing "expired", so severity stays visible.
 *
 * "Claim to work" is a real mutation rather than a UI affordance. Two operators triaging the same
 * queue will otherwise open the same case, and in a maker/checker system that wastes the scarcer
 * resource — a *second* operator who could have confirmed someone else's proposal.
 *
 * The row for a case over the dual-control threshold is marked, so a maker knows before opening
 * it that their approval will need a checker.
 *
 * The filters live in the URL (`@/lib/nav/list-state`), so a triaged view survives opening a case
 * and coming back — and one operator can send another the exact slice they are looking at.
 */
export function ReviewQueuePage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { scope } = useWebScope();
  const view = useListState(REVIEW_LIST);
  const { status, reason, claimed, min: minAmount, amount, q, sort } = view.state;
  // Most-urgent-first for a deadline or an age, largest-first for money.
  const dir = view.state.dir ?? (sort === 'AMOUNT' ? 'DESC' : 'ASC');

  const queue = useReviewQueue({ status, reason, claimed, minAmount, amount, q });
  const claim = useCaseClaim('');

  const rows = queue.data?.items ?? [];
  const breached = queue.data?.breached ?? 0;
  const filtered =
    status !== 'ALL' ||
    reason !== 'ALL' ||
    claimed !== 'ALL' ||
    minAmount !== '' ||
    amount !== '' ||
    q !== '';
  const clearFilters = (): void => {
    view.set({ status: 'ALL', reason: 'ALL', claimed: 'ALL', min: '', amount: '', q: '' });
  };
  const at = nowMs();

  // Sorted here rather than on the server: the projection already returns the whole queue (it is
  // SLA-bounded, not paginated), so reordering it is a display concern. SLA-ascending is the
  // server's own order — the default view is unchanged.
  const sorted = useMemo(() => {
    const sign = dir === 'ASC' ? 1 : -1;
    const value = (row: ReviewQueueRow): number =>
      sort === 'AMOUNT'
        ? Number(row.context.amount.value)
        : sort === 'AGE'
          ? -toEpochMs(row.case.opened_at) // ascending age = the youngest case first
          : toEpochMs(row.context.sla_due_at);
    return [...rows].sort((a, b) => sign * (value(a) - value(b)));
  }, [rows, sort, dir]);

  // Header click: same column flips the direction, a new column starts at its natural order —
  // which, with no `dir` in the URL, is what the derivation above already says.
  const sortBy = (key: string): void => {
    const next = readSort(key);
    if (next === undefined) return;
    view.set(
      next === sort ? { dir: dir === 'ASC' ? 'DESC' : 'ASC' } : { sort: next, dir: undefined },
    );
  };

  const columns: Column<ReviewQueueRow>[] = [
    {
      id: 'case',
      header: t('reviewQueue.columns.case'),
      priority: 'primary',
      cell: (row) => (
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs font-medium">{shortId(row.case.id)}</span>
            <StateBadge machine="review-case" status={row.case.status} size="sm" />
            {row.context.dual_control_required ? (
              <Tooltip content={t('reviewQueue.dualControlTooltip')}>
                <span className="inline-flex cursor-help items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-2xs font-medium text-accent-foreground">
                  <ShieldAlert className="size-3" aria-hidden="true" />
                  {t('reviewQueue.dualControlBadge')}
                </span>
              </Tooltip>
            ) : null}
          </div>
          <p className="truncate font-mono text-2xs text-muted-foreground">
            {shortId(row.case.transaction_id)}
          </p>
        </div>
      ),
    },
    {
      id: 'reason',
      header: t('reviewQueue.columns.reason'),
      priority: 'secondary',
      cell: (row) => {
        const meta = REVIEW_REASON_META[row.case.reason];
        return (
          <Tooltip content={meta ? t(meta.descriptionKey) : row.case.reason}>
            <span className="cursor-help text-xs font-medium">
              {meta ? t(meta.labelKey) : row.case.reason}
            </span>
          </Tooltip>
        );
      },
    },
    {
      id: 'participant',
      header: t('common.webScope.column'),
      priority: 'detail',
      // Every other list on the console's four screens already names the row's site — this is
      // the queue's own gap. It matters most exactly when the console is unscoped, which is when
      // triage mixes cases from every website with nothing else on the row to tell them apart.
      // Since cross-website matching, the depositor's site is only half the story — the withdrawal
      // this case is deciding a payout on can belong to another one entirely.
      cell: (row) => (
        <ParticipantPair
          depositor={row.context.participant}
          withdrawer={row.context.withdrawal_participant}
        />
      ),
    },
    {
      id: 'username',
      header: t('reviewQueue.columns.username'),
      priority: 'detail',
      // The website's display handle for the depositor when it has sent one (`account_display`),
      // else the opaque account_ref it identifies them by. Absent only against a pre-upgrade API.
      cell: (row) =>
        row.context.account_display != null ? (
          <span className="truncate text-xs font-medium">{row.context.account_display}</span>
        ) : row.context.account_ref === undefined ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <span className="truncate font-mono text-xs">{row.context.account_ref}</span>
        ),
    },
    {
      id: 'amount',
      header: t('reviewQueue.columns.amount'),
      align: 'right',
      priority: 'secondary',
      sortKey: 'AMOUNT',
      cell: (row) => <MoneyDisplay amount={row.context.amount} />,
    },
    {
      id: 'sla',
      header: t('reviewQueue.columns.sla'),
      priority: 'secondary',
      sortKey: 'SLA',
      cell: (row) => <SlaCountdown dueAt={row.context.sla_due_at} />,
    },
    {
      id: 'age',
      header: t('reviewQueue.columns.age'),
      priority: 'detail',
      sortKey: 'AGE',
      cell: (row) => (
        <span className="tnum whitespace-nowrap text-xs text-muted-foreground">
          {formatAge(row.case.opened_at, at)}
        </span>
      ),
    },
    {
      id: 'claim',
      header: t('reviewQueue.columns.claimedBy'),
      priority: 'detail',
      cell: (row) =>
        row.context.claimed_by ? (
          <span className="truncate text-xs">{row.context.claimed_by}</span>
        ) : (
          <span className="text-xs text-muted-foreground">{t('reviewQueue.unclaimed')}</span>
        ),
    },
    {
      id: 'action',
      header: '',
      srHeader: t('reviewQueue.columns.actionSrHeader'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => (
        <Button asChild variant="ghost" size="sm">
          <Link
            to="/ops/reviews/$caseId"
            params={{ caseId: row.case.id }}
            search={{ from: view.href }}
          >
            <Hand aria-hidden="true" />
            {row.context.claimed_by ? t('reviewQueue.openCase') : t('reviewQueue.claimAndWork')}
          </Link>
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('reviewQueue.title')}
        description={t('reviewQueue.description')}
        badge={
          breached > 0 ? (
            <Pill className="bg-state-bad-bg text-state-bad">
              <AlarmClock className="size-3" aria-hidden="true" />
              {t('reviewQueue.slaBadge.breached', { count: breached })}
            </Pill>
          ) : (
            <Pill className="bg-state-good-bg text-state-good">
              <ClipboardCheck className="size-3" aria-hidden="true" />
              {t('reviewQueue.slaBadge.clear')}
            </Pill>
          )
        }
      />

      {/* A queue that looked identical scoped and unscoped is exactly how an operator reads one
          site's cases as everyone's. */}
      <WebScopeScopedNotice />

      {claim.isError ? <ErrorState error={claim.error} /> : null}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        <div className="space-y-1.5 xl:col-span-2">
          <Label htmlFor="queue-search">{t('reviewQueue.searchLabel')}</Label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id="queue-search"
              className="pl-9"
              placeholder={t('reviewQueue.searchPlaceholder')}
              value={q}
              onChange={(event) => {
                view.set({ q: event.target.value });
              }}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="queue-status">{t('reviewQueue.statusLabel')}</Label>
          <Select
            value={status}
            onValueChange={(value) => {
              view.set({ status: value });
            }}
          >
            <SelectTrigger id="queue-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{t('reviewQueue.statusOptions.all')}</SelectItem>
              <SelectItem value="OPEN">{t('reviewQueue.statusOptions.open')}</SelectItem>
              <SelectItem value="PROPOSED">{t('reviewQueue.statusOptions.proposed')}</SelectItem>
              <SelectItem value="ESCALATED">{t('reviewQueue.statusOptions.escalated')}</SelectItem>
              <SelectItem value="CONFIRMED">{t('reviewQueue.statusOptions.confirmed')}</SelectItem>
              <SelectItem value="CLOSED">{t('reviewQueue.statusOptions.closed')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="queue-reason">{t('reviewQueue.reasonLabel')}</Label>
          <Select
            value={reason}
            onValueChange={(value) => {
              view.set({ reason: value });
            }}
          >
            <SelectTrigger id="queue-reason">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{t('reviewQueue.reasonAll')}</SelectItem>
              {REVIEW_REASONS.map((value) => {
                const meta = REVIEW_REASON_META[value];
                return (
                  <SelectItem key={value} value={value}>
                    {meta ? t(meta.labelKey) : value}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-3 gap-3 xl:col-span-2">
          <div className="space-y-1.5">
            <Label htmlFor="queue-claimed">{t('reviewQueue.claimLabel')}</Label>
            <Select
              value={claimed}
              onValueChange={(value) => {
                view.set({ claimed: value });
              }}
            >
              <SelectTrigger id="queue-claimed">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t('reviewQueue.claimOptions.any')}</SelectItem>
                <SelectItem value="UNCLAIMED">{t('reviewQueue.claimOptions.unclaimed')}</SelectItem>
                <SelectItem value="CLAIMED">{t('reviewQueue.claimOptions.claimed')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="queue-amount">{t('reviewQueue.minAmountLabel')}</Label>
            <Input
              id="queue-amount"
              inputMode="numeric"
              className="tnum"
              placeholder={t('reviewQueue.minAmountPlaceholder')}
              value={minAmount}
              onChange={(event) => {
                view.set({ min: event.target.value.replace(/[^\d.]/g, '') });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="queue-exact-amount">{t('reviewQueue.amountLabel')}</Label>
            <Input
              id="queue-exact-amount"
              inputMode="numeric"
              className="tnum"
              placeholder={t('reviewQueue.amountPlaceholder')}
              value={amount}
              onChange={(event) => {
                view.set({ amount: event.target.value.replace(/[^\d.]/g, '') });
              }}
            />
          </div>
        </div>

        <SortControl
          id="queue-sort"
          className="md:hidden"
          label={t('reviewQueue.sortLabel')}
          options={[
            { value: 'SLA', label: t('reviewQueue.sortOptions.SLA') },
            { value: 'AGE', label: t('reviewQueue.sortOptions.AGE') },
            { value: 'AMOUNT', label: t('reviewQueue.sortOptions.AMOUNT') },
          ]}
          value={sort}
          dir={dir}
          // Changing the column here keeps the direction the operator chose beside it.
          onValueChange={(value) => {
            const next = readSort(value);
            if (next === undefined) return;
            view.set({ sort: next, dir });
          }}
          onDirChange={(value) => {
            view.set({ dir: value });
          }}
        />
      </div>

      {filtered ? (
        <Button
          variant="ghost"
          size="sm"
          className="-mt-4 w-fit"
          onClick={clearFilters}
        >
          <X aria-hidden="true" />
          {t('reviewQueue.clearFilters')}
        </Button>
      ) : null}

      {queue.isLoading ? (
        <TableSkeleton rows={8} columns={7} />
      ) : queue.isError ? (
        <ErrorState
          error={queue.error}
          onRetry={() => {
            void queue.refetch();
          }}
        />
      ) : (
        <>
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {t('reviewQueue.caseCount', { count: rows.length })}
            {filtered ? ` ${t('reviewQueue.matchingFilters')}` : ''}
          </p>
          <DataTable
            caption={t('reviewQueue.tableCaption')}
            columns={columns}
            rows={sorted}
            rowKey={(row) => row.case.id}
            sort={{ key: sort, dir, onChange: sortBy }}
            // The row and the button inside it must agree: a click on the button bubbles to the
            // row, and the row's navigation is the one that lands — so it has to carry the same
            // return address, or opening a case by its button would still lose the filters.
            onRowClick={(row) => {
              void navigate({
                to: '/ops/reviews/$caseId',
                params: { caseId: row.case.id },
                search: { from: view.href },
              });
            }}
            className={cn(rows.length === 0 && 'hidden')}
            empty={
              <EmptyState
                icon={<ClipboardCheck className="size-5" aria-hidden="true" />}
                title={
                  filtered
                    ? t('reviewQueue.empty.filteredTitle')
                    : scope !== ALL_WEBS
                      ? t('common.webScope.emptyTitle')
                      : t('reviewQueue.empty.clearTitle')
                }
                description={
                  filtered
                    ? t('reviewQueue.empty.filteredDescription')
                    : // A filtered-empty queue already explains itself; an unfiltered empty one, when
                      // scoped, needs a different explanation — "this site has nothing open", not
                      // "the queue is clear" — or an operator reads their one site's calm as the
                      // platform's.
                      scope !== ALL_WEBS
                      ? t('common.webScope.emptyDescription', { web: scope })
                      : t('reviewQueue.empty.clearDescription')
                }
                action={
                  filtered ? (
                    <Button
                      variant="outline"
                      onClick={clearFilters}
                    >
                      {t('reviewQueue.clearFilters')}
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </>
      )}

      <p className="text-2xs leading-relaxed text-muted-foreground">
        {t('reviewQueue.footer.base')}
        {rows.length > 0 && rows.some((r) => toEpochMs(r.context.sla_due_at) < at)
          ? ` ${t('reviewQueue.footer.breachedSuffix')}`
          : ''}
      </p>
    </>
  );
}
